import { useState, useEffect, useCallback, useMemo } from 'react';
import { SignifyDistributor, DEFAULT_SIGNIFY_CUSTOMER_ID } from '#types/SignifyApi.types';
import { getDistributors } from '../services/signifyApiService';

/**
 * Custom hook for fetching and managing Signify distributors
 * Provides search functionality and filtering capabilities
 * Will eventually be replaced with Firestore real-time data
 * @param customerId The customer ID to fetch distributors for
 * @param autoLoad Whether to automatically load distributors on mount (default: false for performance)
 * @returns Object containing distributors, loading state, error, and utility functions
 */
export const useSignifyDistributors = (
    customerId: string = DEFAULT_SIGNIFY_CUSTOMER_ID,
    autoLoad: boolean = false
) => {
    const [distributors, setDistributors] = useState<SignifyDistributor[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [hasLoaded, setHasLoaded] = useState(false);

    /**
     * Fetch distributors from API and sort with default distributor first
     * Prevents duplicate fetches if already loading or loaded
     */
    const fetchDistributors = useCallback(async () => {
        // Prevent duplicate fetches if already loading or has data
        if (loading || (hasLoaded && distributors.length > 0)) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await getDistributors(customerId, 'FULL');

            if (response && response.distributors) {
                const sortedDistributors = response.distributors.sort((a, b) => {
                    // Put default distributor first
                    const aIsDefault = a.uid === DEFAULT_SIGNIFY_CUSTOMER_ID;
                    const bIsDefault = b.uid === DEFAULT_SIGNIFY_CUSTOMER_ID;

                    if (aIsDefault && !bIsDefault) return -1;
                    if (!aIsDefault && bIsDefault) return 1;

                    // Then sort alphabetically
                    return a.name.localeCompare(b.name);
                });

                setDistributors(sortedDistributors);
                setHasLoaded(true);
            } else {
                setDistributors([]);
                setHasLoaded(true);
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch distributors';
            setError(errorMessage);
            setHasLoaded(true);
            console.error('Error fetching distributors:', err);
        } finally {
            setLoading(false);
        }
    }, [customerId, loading, hasLoaded, distributors.length]);

    /**
     * Load distributors on mount only if autoLoad is true
     */
    useEffect(() => {
        if (autoLoad && !hasLoaded) {
            fetchDistributors();
        }
    }, [autoLoad, hasLoaded, fetchDistributors]);

    /**
     * Filter distributors based on search term
     * Searches across name, UID, display UID, town, and address fields (city, street, postal code)
     * @returns Filtered array of distributors matching the search term
     */
    const filteredDistributors = useMemo(() => {
        if (!searchTerm || searchTerm.trim() === '') {
            return distributors;
        }

        const lowerSearchTerm = searchTerm.toLowerCase().trim();
        
        return distributors.filter(distributor => {
            if (distributor.name.toLowerCase().includes(lowerSearchTerm)) {
                return true;
            }
            
            if (distributor.uid.toLowerCase().includes(lowerSearchTerm)) {
                return true;
            }
            
            if (distributor.uidForDisplay && 
                distributor.uidForDisplay.toLowerCase().includes(lowerSearchTerm)) {
                return true;
            }
            
            if (distributor.town && 
                distributor.town.toLowerCase().includes(lowerSearchTerm)) {
                return true;
            }
            
            if (distributor.address) {
                const address = distributor.address;
                if (
                    (address.city && address.city.toLowerCase().includes(lowerSearchTerm)) ||
                    (address.street && address.street.toLowerCase().includes(lowerSearchTerm)) ||
                    (address.postalCode && address.postalCode.toLowerCase().includes(lowerSearchTerm))
                ) {
                    return true;
                }
            }
            
            return false;
        });
    }, [distributors, searchTerm]);

    /**
     * Refresh distributors by fetching fresh data from API
     */
    const refreshDistributors = useCallback(async () => {
        setHasLoaded(false);
        await fetchDistributors();
    }, [fetchDistributors]);

    /**
     * Load distributors if not already loaded (for lazy loading)
     */
    const loadDistributorsIfNeeded = useCallback(async () => {
        if (!hasLoaded && !loading) {
            await fetchDistributors();
        }
    }, [hasLoaded, loading, fetchDistributors]);

    return {
        distributors: filteredDistributors,
        allDistributors: distributors,
        loading,
        error,
        searchTerm,
        setSearchTerm,
        refreshDistributors,
        loadDistributorsIfNeeded,
        hasLoaded,
        totalCount: distributors.length,
        filteredCount: filteredDistributors.length
    };
};